import api from './client';
import type {
  InvitationCreate,
  InvitationResponse,
  InvitationValidateResponse,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  SendGuestCodeRequest,
  SendGuestCodeResponse,
  GuestLoginRequest,
  GuestLoginResponse,
  IntegrityReport,
  IntegrityFixRequest,
} from './types';

export const invitationsApi = {
  /**
   * Create an invitation to join the organization
   * @param invitation - Invitation data
   */
  create: async (invitation: InvitationCreate): Promise<InvitationResponse> => {
    const { data } = await api.post('/invitations', invitation);
    return data;
  },

  /**
   * Validate an invitation without authentication
   * @param token - Web invitation token (optional)
   * @param code - Mobile invitation code (optional)
   */
  validate: async (params: { token?: string; code?: string }): Promise<InvitationValidateResponse> => {
    const { data } = await api.get('/invitations/validate', { params });
    return data;
  },

  /**
   * Accept an invitation and join the organization as a full member
   * @param request - Accept invitation request (token or code)
   */
  accept: async (request: AcceptInvitationRequest): Promise<AcceptInvitationResponse> => {
    const { data } = await api.post('/invitations/accept', request);
    return data;
  },

  /**
   * Self-service repair (no code needed): if a pending invitation matches the
   * authenticated user's email, link it automatically. Rejects with 404 when
   * there is nothing to auto-claim — the caller should then prompt for a code
   * and use {@link claim}.
   */
  autoClaim: async (): Promise<AcceptInvitationResponse> => {
    const { data } = await api.post('/invitations/auto-claim');
    return data;
  },

  /**
   * Self-service repair: link the currently authenticated account to an
   * invitation via its token (web) or code (mobile). Used when the login email
   * differs from the invitation email (Google/Apple mismatch).
   * @param request - Accept invitation request (token or code)
   */
  claim: async (request: AcceptInvitationRequest): Promise<AcceptInvitationResponse> => {
    const { data } = await api.post('/invitations/claim', request);
    return data;
  },

  /**
   * Admin tool: relink a stuck invited user to the Firebase account the person
   * actually signs in with (looked up by login email).
   * @param invitedUserId - Doc ID of the pending/invited user to repair
   * @param loginEmail - Email of the account the person actually logs in with
   */
  relink: async (
    invitedUserId: string,
    loginEmail: string,
  ): Promise<AcceptInvitationResponse> => {
    const { data } = await api.post('/invitations/relink', {
      invited_user_id: invitedUserId,
      login_email: loginEmail,
    });
    return data;
  },

  /**
   * Send a verification code to a guest for passwordless auth
   * @param request - Send guest code request
   */
  sendGuestCode: async (request: SendGuestCodeRequest): Promise<SendGuestCodeResponse> => {
    const { data } = await api.post('/invitations/send-guest-code', request);
    return data;
  },

  /**
   * Guest authentication - first-time acceptance or returning login
   * @param request - Guest login request
   */
  guestAuth: async (request: GuestLoginRequest): Promise<GuestLoginResponse> => {
    const { data } = await api.post('/invitations/guest-auth', request);
    return data;
  },

  /**
   * List all invitations for the user's organization
   */
  list: async (): Promise<InvitationResponse[]> => {
    const { data } = await api.get('/invitations');
    return data;
  },

  /**
   * Cancel a pending invitation
   * @param invitationId - Invitation ID
   */
  cancel: async (invitationId: string): Promise<{ message: string }> => {
    const { data } = await api.delete(`/invitations/${invitationId}`);
    return data;
  },

  /**
   * Resend invitation email for a user with pending status
   * @param userId - User ID
   */
  resend: async (userId: string): Promise<{ message: string }> => {
    const { data } = await api.post(`/invitations/resend/${userId}`);
    return data;
  },

  /**
   * Reopen an invitation so its link works again (admin "reactivate" control).
   * Issues a fresh token, resets the invitation to `pending`, and returns the
   * updated invitation — including the new `invitation_url` — WITHOUT sending
   * an email. Use to unstick an invitation that was accepted (or auto-accepted
   * on login) but whose invitee never onboarded, so their link now fails with
   * "déjà utilisée". Does not revoke the invitee's existing access.
   * @param invitationId - Invitation ID
   */
  reopen: async (invitationId: string): Promise<InvitationResponse> => {
    const { data } = await api.post(`/invitations/${invitationId}/reopen`);
    return data;
  },

  /**
   * Admin: scan the org's projects for client contacts linked to broken
   * accounts (deleted/orphan, inactive duplicate, no login provider, or
   * missing guest access). Read-only.
   */
  integrity: async (): Promise<IntegrityReport> => {
    const { data } = await api.get('/invitations/integrity');
    return data;
  },

  /**
   * Admin: apply one integrity fix (repoint a contact to the client's live
   * account, grant guest access, or send a set-password email).
   */
  fixIntegrity: async (req: IntegrityFixRequest): Promise<{ message: string }> => {
    const { data } = await api.post('/invitations/integrity/fix', req);
    return data;
  },
};
